import React, { useEffect, useMemo, useRef, useState } from "react";
import dashboardData from "../data/private/dashboard.json";
import billingCopy from "../data/private/billing.json";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import KeyValueTable from "../components/ui/KeyValueTable";
import { logActivity } from "../utils/activity";
import normalizeApiError from "../utils/normalizeApiError";
import SuccessToast from "../components/ui/SuccessToast";
import useSingleFlight from "../utils/useSingleFlight";
import InfoBanner from "../components/InfoBanner";
import { usePlatformContext } from "../context/PlatformContext";
import { createPaymentIntent, getStatus, getUsage } from "../api/billing.api";

export default function DashboardBilling() {
  const [paymentOption, setPaymentOption] = useState(
    billingCopy.paymentOptions[0].id
  );
  const [gatewayStatus, setGatewayStatus] = useState("idle");
  const [manualPending, setManualPending] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(
    billingCopy.gateway.options[0]
  );
  const { usage, session, user } = usePlatformContext();
  const [statusData, setStatusData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [billingError, setBillingError] = useState(null);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState("");
  const billingRequestedRef = useRef(false);
  const [registerForm, setRegisterForm] = useState({
    amount: "",
    mode: "",
    reference: "",
  });
  const { run: runPayment, isRunning: paymentLocked } = useSingleFlight();

  const billingSummary = useMemo(() => {
    if (!usage && !session) return null;
    return {
      plan: usage?.plan ?? "—",
      remainingCredits:
        usage?.analysisRemaining === 0 || usage?.analysisRemaining
          ? usage.analysisRemaining
          : "—",
      requiresPayment: Boolean(session?.requiresPayment),
      pendingPayment: Boolean(session?.forceAction),
    };
  }, [session, usage]);

  useEffect(() => {
    if (billingRequestedRef.current) {
      return;
    }
    billingRequestedRef.current = true;
    const fetchBilling = async () => {
      try {
        const data = await getStatus();
        setStatusData(data || null);
      } catch (error) {
        setBillingError(normalizeApiError(error));
      }
      try {
        const data = await getUsage();
        setUsageData(data || null);
      } catch (error) {
        setBillingError((prev) => prev || normalizeApiError(error));
      }
    };
    fetchBilling();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          {billingCopy.title}
        </h1>
        <p className="mt-2 text-sm text-slate-600">{billingCopy.subtitle}</p>
      </div>

      <div className="card card-hover p-4">
        <p className="text-sm font-semibold text-slate-900">
          {billingCopy.subtitle}
        </p>
        <div className="mt-3 flex flex-wrap gap-3">
          {billingCopy.paymentOptions.map((option) => (
            <label
              key={option.id}
              className="flex items-center gap-2 text-sm text-slate-600"
            >
              <input
                type="radio"
                name="payment-option"
                checked={paymentOption === option.id}
                onChange={() => {
                  setPaymentOption(option.id);
                  logActivity("payment_option_selected", { option: option.id });
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      {billingSummary?.requiresPayment && (
        <InfoBanner
          title="Payment required"
          description="Your plan needs payment before you can continue."
        />
      )}

      {billingSummary?.pendingPayment && (
        <InfoBanner
          title="Payment pending"
          description="Your payment is pending confirmation."
        />
      )}

      {billingSummary && !billingSummary.requiresPayment && (
        <div className="card p-4">
          <p className="text-sm font-semibold text-slate-900">Plan status</p>
          <p className="mt-1 text-sm text-slate-600">
            Plan: {billingSummary.plan}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Remaining credits: {billingSummary.remainingCredits}
          </p>
        </div>
      )}

      <InfoBanner stateKey="billingInfo" />

      {paymentOption === "manual" && (
        <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
          <div className="card card-hover p-6">
            <p className="text-sm font-semibold text-slate-900">
              {billingCopy.manual.qrLabel}
            </p>
            <div className="mt-4 flex h-40 items-center justify-center rounded-xl border border-dashed border-slate-200 text-xs text-slate-500">
              {billingCopy.manual.qrLabel}
            </div>
          </div>
          <div className="card card-hover p-6">
            <p className="text-sm font-semibold text-slate-900">
              {billingCopy.manual.bankTitle}
            </p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {billingCopy.manual.bankDetails.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
          <div className="card card-hover p-6 md:col-span-2">
            <p className="text-sm font-semibold text-slate-900">
              {billingCopy.manual.stepsTitle}
            </p>
            <ol className="mt-3 space-y-2 text-sm text-slate-600">
              {billingCopy.manual.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                className="btn btn-secondary"
                href={`https://wa.me/?text=${encodeURIComponent(
                  billingCopy.manual.whatsappText
                )}`}
                target="_blank"
                rel="noreferrer"
              >
                {billingCopy.manual.whatsappLabel}
              </a>
              <button
                className="btn btn-primary disabled:cursor-not-allowed disabled:opacity-60"
                disabled={manualPending || paymentLocked}
                data-testid="dashboard-billing-pay-btn"
                onClick={() =>
                  runPayment(async () => {
                    setManualPending(true);
                    logActivity("manual_payment_completed");
                    logActivity("payment_pending");
                    const payload = {
                      userId: user?.id || "",
                      amount: Number(registerForm.amount || 0),
                      paymentReference: registerForm.reference,
                      mode: registerForm.mode || "manual",
                    };
                    if (!payload.userId || !payload.amount || !payload.paymentReference) {
                      setBillingError(
                        normalizeApiError(
                          "Payment registration requires userId, amount, and paymentReference."
                        )
                      );
                      return;
                    }
                    try {
                      const data = await createPaymentIntent(payload);
                      setPaymentResponse(data || null);
                      setPaymentSuccess("Payment registration submitted successfully.");
                      setBillingError(null);
                    } catch (error) {
                      setBillingError(normalizeApiError(error));
                    }
                  })
                }
              >
                {billingCopy.manual.completedLabel}
              </button>
            </div>
            {paymentSuccess && (
              <div className="mt-4">
                <SuccessToast
                  message={paymentSuccess}
                  onDismiss={() => setPaymentSuccess("")}
                  testId="dashboard-billing-success"
                />
              </div>
            )}
            {manualPending && (
              <div className="mt-4 card p-4">
                <p className="text-sm font-semibold text-slate-900">
                  {billingCopy.manual.pendingTitle}
                </p>
                <p className="mt-2 text-sm text-slate-600">
                  {billingCopy.manual.pendingDescription}
                </p>
              </div>
            )}
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs text-slate-500">Amount</label>
                <input
                  className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Enter amount"
                  value={registerForm.amount}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, amount: event.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">Mode</label>
                <input
                  className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Enter mode"
                  value={registerForm.mode}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, mode: event.target.value }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">Reference</label>
                <input
                  className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  placeholder="Enter reference"
                  value={registerForm.reference}
                  onChange={(event) =>
                    setRegisterForm((prev) => ({ ...prev, reference: event.target.value }))
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {paymentOption === "gateway" && (
        <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
          <div className="card card-hover p-6">
            <p className="text-sm font-semibold text-slate-900">
              {billingCopy.gateway.optionsTitle}
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              {billingCopy.gateway.options.map((option) => (
                <button
                  key={option}
                  className={`btn ${
                    selectedGateway === option ? "btn-primary" : "btn-secondary"
                  }`}
                  onClick={() => setSelectedGateway(option)}
                >
                  {option}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Blocked — backend API not defined in implementation doc.
            </p>
          </div>
          <div className="card card-hover p-6">
            <p className="text-sm font-semibold text-slate-900">
              {billingCopy.gateway.summaryTitle}
            </p>
            <p className="mt-2 text-sm text-slate-600">
              {dashboardData.billing.subtitle}
            </p>
            <button
              className="btn btn-primary mt-4 w-full"
              disabled
              onClick={() => {
                setGatewayStatus("success");
                logActivity("gateway_payment_attempt", {
                  gateway: selectedGateway,
                });
              }}
            >
              {billingCopy.gateway.proceedLabel}
            </button>
            {gatewayStatus === "success" && (
              <div className="mt-4 card p-4">
                <p className="text-sm font-semibold text-slate-900">
                  {billingCopy.gateway.successTitle}
                </p>
                <p className="mt-2 text-sm text-slate-600">
                  {billingCopy.gateway.successDescription}
                </p>
              </div>
            )}
            {gatewayStatus === "failure" && (
              <div className="mt-4 card p-4">
                <p className="text-sm font-semibold text-slate-900">
                  {billingCopy.gateway.failureTitle}
                </p>
                <p className="mt-2 text-sm text-slate-600">
                  {billingCopy.gateway.failureDescription}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {statusData ? (
        <KeyValueTable data={statusData} title="Billing status" />
      ) : (
        <EmptyState
          title="No billing status yet"
          message="Billing status will appear once available."
          testId="dashboard-empty-billing-status"
        />
      )}
      {usageData ? (
        <KeyValueTable data={usageData} title="Billing usage" />
      ) : (
        <EmptyState
          title="No usage data yet"
          message="Usage data will appear once available."
          testId="dashboard-empty-billing-usage"
        />
      )}
      {paymentResponse && (
        <KeyValueTable data={paymentResponse} title="Payment response" />
      )}
      {billingError && <ErrorState error={billingError} />}
    </div>
  );
}
